// Centralized helper so every CTA on the landing site lands on the same
// app endpoint. CORE-40 added a Russia-only mirror (app-ru.agapella.io)
// fronted by a Moscow nginx — going there directly when we can tell the
// visitor is in Russia saves the one extra Vercel-edge 307 RTT that
// app.agapella.io would have issued anyway.
//
// CORE-940 — every agapella.io host now serves the app itself (the apex stopped
// redirecting), so a visitor who is already on one of ours stays where they
// are: sending them across origins would drop the session cookie's origin pin
// and cost a full page load for nothing. The guess below only decides where to
// send someone who is NOT on an agapella.io host — a preview deploy, say.
//
// Detection is best-effort: timezone (most reliable) or Cyrillic-language
// preference. We do NOT want to gate on `i18n.language` because the
// language switcher exists explicitly so a Russian visitor can read in
// English without losing the routing benefit, and vice versa.

import { Platform, Linking } from 'react-native';

const APP_BASE_URL = 'https://agapella.io';
const APP_BASE_URL_RU = 'https://app-ru.agapella.io';

// Full list of IANA tz database zones currently used inside Russia.
// Comes from the tzdata `etcetera/zone1970.tab` — kept inline to avoid a
// runtime intl-data dependency on the landing bundle.
const RU_TIMEZONES = new Set<string>([
  'Europe/Moscow',
  'Europe/Kaliningrad',
  'Europe/Samara',
  'Europe/Volgograd',
  'Europe/Astrakhan',
  'Europe/Saratov',
  'Europe/Ulyanovsk',
  'Europe/Kirov',
  'Europe/Simferopol',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Novokuznetsk',
  'Asia/Tomsk',
  'Asia/Barnaul',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Chita',
  'Asia/Yakutsk',
  'Asia/Khandyga',
  'Asia/Vladivostok',
  'Asia/Ust-Nera',
  'Asia/Magadan',
  'Asia/Sakhalin',
  'Asia/Srednekolymsk',
  'Asia/Kamchatka',
  'Asia/Anadyr',
]);

/**
 * Best-effort guess at whether the visitor is in Russia. Used only to
 * pick between two app endpoints — never for content gating or locale.
 * False negatives just cost the user a single 307 redirect, which is the
 * same path they would have taken without this helper.
 */
export function isLikelyRussianUser(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && RU_TIMEZONES.has(tz)) return true;
  } catch {
    // Some embedded webviews lack full Intl — fall through to language.
  }
  const lang = (typeof navigator !== 'undefined' && navigator.language) || '';
  if (lang.toLowerCase().startsWith('ru')) return true;
  return false;
}

export function getAppBaseUrl(): string {
  // Already on one of our hosts (apex, app, app-ru) — all of them serve the app
  // since CORE-940, so stay in place.
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'agapella.io' || host.endsWith('.agapella.io')) return window.location.origin;
  }
  return isLikelyRussianUser() ? APP_BASE_URL_RU : APP_BASE_URL;
}

export function openApp(path: string): void {
  const url = `${getAppBaseUrl()}${path}`;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.href = url;
    return;
  }
  Linking.openURL(url).catch(() => {});
}
